import type { Metadata } from 'next';
import type { Brand } from '@/lib/cms/config';
import { BRAND } from '@/lib/cms/config';
import { localeConfig } from '@/lib/configuration/configuration';
import { getSeoConstants, getBaseUrl } from './constants';
import { buildCanonicalPath } from './canonical';
import { buildHreflangAlternates } from './hreflang';
import { shouldIndex } from './indexingRules';

export interface PageMetadataOptions {
  /** Page title (before suffix). Falls back to brand default. */
  title?: string | null;
  /** Meta description. Falls back to brand default. */
  description?: string | null;
  /** OG/thumbnail image URL */
  ogImage?: string | null;
  /** Current locale slug (e.g. `us-en`) */
  locale: string;
  /** Full URL path including locale (e.g. `/us-en/about`) */
  path: string;
  /** CMS-provided canonical override */
  cmsCanonical?: string | null;
  /** Whether to include hreflang alternates (default: true) */
  includeHreflang?: boolean;
  /** Optional CMS locales filter for hreflang */
  activeLocales?: Set<string> | null;
  /** robots indexing override */
  noIndex?: boolean;
  /** robots follow override */
  noFollow?: boolean;
  /**
   * Skip appending the brand title suffix.
   * Per spec §2: boutique list pages already include the brand name
   * in the title and should not receive the `| {BrandName}` suffix.
   */
  skipSuffix?: boolean;
  /** Brand override (defaults to build-time BRAND) */
  brand?: Brand;
}

/**
 * Append the brand title suffix if not already present.
 * Per spec §2: deduplicates suffix to avoid "About | Gallet | Gallet".
 */
export function appendTitleSuffix(title: string, suffix: string): string {
  if (title.includes(suffix)) return title;
  return `${title} ${suffix}`;
}

/**
 * Build a complete Next.js Metadata object for a page.
 *
 * Handles per spec:
 * - §2: Title suffix, deduplication, fallback
 * - §3: Description fallback chain
 * - §5: Canonical URL (via path or CMS override)
 * - §6: Hreflang alternates (BCP 47, x-default)
 * - §8: OG mirrors title/description, thumbnail meta
 */
export function buildPageMetadata({
  title,
  description,
  ogImage,
  locale,
  path,
  cmsCanonical,
  includeHreflang = true,
  activeLocales,
  noIndex,
  noFollow,
  skipSuffix = false,
  brand = BRAND,
}: PageMetadataOptions): Metadata {
  const constants = getSeoConstants(brand);
  const baseUrl = getBaseUrl(brand);

  let resolvedTitle: string;
  if (!title) {
    resolvedTitle = constants.fallbackTitle;
  } else if (skipSuffix) {
    resolvedTitle = title;
  } else {
    resolvedTitle = appendTitleSuffix(title, constants.titleSuffix);
  }

  const resolvedDescription = description || constants.fallbackDescription;

  const canonicalPath = buildCanonicalPath(
    cmsCanonical ? normalizeCmsCanonical(cmsCanonical, locale) : path
  );

  const resolvedOgImage = ogImage || `${baseUrl}${constants.logoUrl}`;

  const pagePath = extractPagePath(path, locale);

  const alternates: Metadata['alternates'] = {
    canonical: canonicalPath,
  };

  if (includeHreflang) {
    alternates.languages = buildHreflangAlternates(pagePath, baseUrl, activeLocales);
  }

  const { locale: parsedLocale } = localeConfig.parseLocale(locale);
  const ogLocale = parsedLocale.locale.replace('-', '_');

  const autoNoIndex = !shouldIndex({ pathname: path });
  const resolvedNoIndex = noIndex ?? (autoNoIndex || undefined);

  const metadata: Metadata = {
    title: { absolute: resolvedTitle },
    description: resolvedDescription,
    alternates,
    openGraph: {
      title: resolvedTitle,
      description: resolvedDescription,
      url: canonicalPath,
      locale: ogLocale,
      images: resolvedOgImage ? [{ url: resolvedOgImage }] : undefined,
    },
    other: {
      thumbnail: resolvedOgImage,
    },
  };

  if (resolvedNoIndex !== undefined || noFollow !== undefined) {
    metadata.robots = {
      index: !resolvedNoIndex,
      follow: !noFollow,
    };
  }

  return metadata;
}

/**
 * Normalize a CMS-provided canonical value before building the full path.
 *
 * Handles three cases:
 * - Absolute URL (`https://...`) → used as-is (buildCanonicalPath will parse it)
 * - Leading-slash relative (`/about/history`) → prefixed with locale once
 * - Bare slug (`about/history`) → prefixed with locale + leading slash
 */
export function normalizeCmsCanonical(cmsCanonical: string, locale: string): string {
  if (cmsCanonical.startsWith('http')) return cmsCanonical;
  const cleaned = cmsCanonical.replace(/^\/+/, '');
  return `/${locale}/${cleaned}`;
}

/**
 * Extract the page path (without locale prefix) from a full path.
 * E.g. `/us-en/about/history` → `/about/history`
 */
function extractPagePath(fullPath: string, locale: string): string {
  const prefix = `/${locale}`;
  if (fullPath.startsWith(prefix)) {
    const rest = fullPath.slice(prefix.length);
    return rest || '/';
  }
  return fullPath;
}
